import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainLayout from "../components/Layout/MainLayout";
import { CreditCard, Smartphone, Building, Plus, Zap, RefreshCw, Home } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { formatTSh } from "@/lib/currency";
import RechargeLinkDialog from "@/components/RechargeLinkDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PaymentMethodSelector from "@/components/PaymentMethodSelector";
import { usePricingRate } from "@/hooks/usePricing";
import { getUserApartmentId, getApartmentSmartMeter, fetchUnitCompletedTopups } from "@/lib/apartment";
import { queryLiveMeterBalance } from "@/lib/queryLiveMeterBalance";
import { useTenantView } from "@/contexts/TenantViewContext";

// DLT645 encoding functions
const reverseMeterNo = (num: string): string => 
  num.match(/../g)!.reverse().map(b => b.split('').join('')).join('');

const encodeDlt645 = (hex: string): string => 
  hex.match(/../g)!.reverse().map(byte => 
    (parseInt(byte, 16) + 0x33).toString(16).padStart(2, '0')
  ).join('');

const getTimestamp = (): string => 
  new Date().toISOString().slice(2, 10).replace(/-/g, '') + 
  new Date().toISOString().slice(11, 19).replace(/:/g, '') + '00';

const calculateChecksum = (frameBodyHex: string): string => {
  const bytes: string[] = frameBodyHex.match(/../g) || [];
  let sum = 0;
  for (const byte of bytes) {
    sum += parseInt(byte, 16);
  }
  return (sum % 256).toString(16).toUpperCase().padStart(2, '0');
};

const decodeDlt645 = (hex: string): string => 
  hex.match(/../g)!.reverse().map(byte => 
    (parseInt(byte, 16) - 0x33).toString(16).padStart(2, '0')
  ).join('');

const PREAMBLE = 'FEFEFEFE';
const START_OF_FRAME = '68';
const END_SYMBOL = '16';

const TopUp: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [currentBalanceKwh, setCurrentBalanceKwh] = useState(0);
  const [lastTopupDate, setLastTopupDate] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const [isReadingBalance, setIsReadingBalance] = useState(false);
  const [isReadingMeter, setIsReadingMeter] = useState(false);
  const [meterReadDialogOpen, setMeterReadDialogOpen] = useState(false);
  const [meterReadResult, setMeterReadResult] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [queryingLive, setQueryingLive] = useState(false);
  const [userMeterId, setUserMeterId] = useState<string | null>(null);
  const [topUpTarget, setTopUpTarget] = useState<{
    apartmentId: string;
    unitLabel: string;
    buildingName: string | null;
    meterId: string;
    meterNumber: string;
    stsMeterNo: string | null;
    isZhongyi: boolean;
    isSts: boolean;
  } | null>(null);

  // Get dynamic pricing rate from user's building (fallback 400 matches Dashboard)
  const PRICE_PER_KWH = usePricingRate(400);
  const { viewingApartmentId } = useTenantView();

  // Fetch recent transactions and electricity balance from meter
  const fetchData = async (showRefreshState = false) => {
    if (!user) return;

    if (showRefreshState) setIsRefreshing(true);

    try {
      // Unit history: all completed top-ups for this apartment's meter (any payer)
      const { data: transactions, error: transError } = await fetchUnitCompletedTopups({
        userId: user.id,
        viewingApartmentId,
        limit: 10,
        select: "id, amount, reference, created_at, completed_at, user_id, meter_id",
      });

      if (transError) {
        console.error("Error fetching transactions:", transError);
      }

      if (transactions && transactions.length > 0) {
        const sorted = transactions
          .map((tx: any) => ({
            ...tx,
            payment_method: "Mobile Money",
            effective_at: tx.completed_at || tx.created_at,
          }))
          .slice(0, 5);

        setRecentTransactions(sorted);
        setLastTopupDate(sorted[0].effective_at);
      } else {
        setRecentTransactions([]);
        setLastTopupDate(null);
      }

      // Resolve apartment: tenant-view override, then primary / co-tenant / owner link
      const apartmentId = viewingApartmentId ?? await getUserApartmentId(user.id);

      if (!apartmentId) {
        setUserMeterId(null);
        setTopUpTarget(null);
        setCurrentBalance(0);
        setCurrentBalanceKwh(0);
        return;
      }

      const { data: apartmentRow } = await supabase
        .from("apartments")
        .select("id, apartment_number, smart_meter_id, buildings(name)")
        .eq("id", apartmentId)
        .maybeSingle();

      // Prefer apartments.smart_meter_id, else newest-reading meter (handles multi-meter apts)
      const meterData = await getApartmentSmartMeter(
        apartmentId,
        "id, meter_number, sts_meter_no, compliance_type, deveui, manufacturers(slug)",
      );

      if (!meterData?.id) {
        setUserMeterId(null);
        setTopUpTarget(null);
        setCurrentBalance(0);
        setCurrentBalanceKwh(0);
        return;
      }

      setUserMeterId(meterData.id);
      const mfrSlug = meterData.manufacturers?.slug || null;
      const isZhongyi = mfrSlug === "zhongyi" || !!(meterData.deveui && String(meterData.deveui).trim());
      const isSts = (meterData.compliance_type || (isZhongyi ? "STS" : "")).toUpperCase() === "STS";
      const buildingName = (apartmentRow as any)?.buildings?.name ?? null;
      setTopUpTarget({
        apartmentId,
        unitLabel: apartmentRow?.apartment_number || "—",
        buildingName,
        meterId: meterData.id,
        meterNumber: meterData.meter_number || "—",
        stsMeterNo: meterData.sts_meter_no || null,
        isZhongyi,
        isSts,
      });

      const { data: balanceReading, error: balanceError } = await supabase
        .from("meter_readings")
        .select("electricity_balance_kwh, reading_datetime")
        .eq("meter_id", meterData.id)
        .not("electricity_balance_kwh", "is", null)
        .order("reading_datetime", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!balanceError && balanceReading?.electricity_balance_kwh != null) {
        const availableKwh = Number(balanceReading.electricity_balance_kwh);
        setCurrentBalanceKwh(availableKwh);
        setCurrentBalance(availableKwh * PRICE_PER_KWH);
      } else {
        setCurrentBalanceKwh(0);
        setCurrentBalance(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingTransactions(false);
      if (showRefreshState) setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, PRICE_PER_KWH, refreshKey, viewingApartmentId]);

  // Subscribe to realtime updates on user_topups table
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('topup-updates')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT and UPDATE
          schema: 'public',
          table: 'user_topups',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[TopUp] Realtime update received:', payload);
          // Refresh data when a topup is inserted or updated
          fetchData(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Auto-refresh balance after successful payment (poll a few times to catch webhook update)
  const refreshBalanceAfterPayment = () => {
    toast({
      title: t('topup.updatingBalance'),
      description: t('topup.updatingBalanceDesc'),
    });

    // Poll for updated balance: 5s, 10s, 20s, 30s after payment
    const delays = [5000, 10000, 20000, 30000];
    delays.forEach((delay) => {
      setTimeout(() => {
        console.log(`[TopUp] Polling for balance update after ${delay}ms`);
        setRefreshKey(prev => prev + 1);
      }, delay);
    });
  };

  const quickAmounts = [20000, 50000, 100000, 150000, 200000, 500000];
  /** ClickPesa USSD/card limits (TZS) */
  const MIN_TOPUP_TZS = 500;
  const MAX_TOPUP_TZS = 3_000_000;

  const getSelectedAmount = () => {
    return selectedAmount || parseInt(customAmount, 10) || 0;
  };

  const selectedAmt = getSelectedAmount();
  const amountOutOfRange =
    selectedAmt > 0 && (selectedAmt < MIN_TOPUP_TZS || selectedAmt > MAX_TOPUP_TZS);
  const amountValid =
    selectedAmt >= MIN_TOPUP_TZS && selectedAmt <= MAX_TOPUP_TZS;

  const getKwhFromAmount = (amount: number): number => {
    return amount / PRICE_PER_KWH;
  };

  const handleZeroBalance = async () => {
    if (!user) return;

    // Note: The zero balance command doesn't return balance data in its response
    // Use handleSinglePhaseRead instead to get current balance
    toast({
      title: "ℹ️ Use Read Meter Instead",
      description: "The zero balance command doesn't return balance data. Use 'Read Meter Data' button to see current balance.",
    });
  };

  const handleSinglePhaseRead = async () => {
    if (!user) return;

    setIsReadingMeter(true);
    try {
      const controlCode = '11';
      const dataLength = '04';
      const dataIdentification = '04602001';
      const encodedDataId = encodeDlt645(dataIdentification);

      const meterNumber = '002024023571';
      const password = '00000002';
      const operatorCode = '30303030';
      const pKey = 'TW0003';

      const reverseMeterNum = reverseMeterNo(meterNumber);
      const bodyPayload = 
        START_OF_FRAME + 
        reverseMeterNum + 
        START_OF_FRAME + 
        controlCode + 
        dataLength + 
        encodedDataId;
      
      const checksum = calculateChecksum(bodyPayload);
      const payload = (PREAMBLE + bodyPayload + checksum + END_SYMBOL).toUpperCase();

      const mqttTopic = `/device/electricmeter/${pKey}/${meterNumber}/data/down`;

      const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
        body: {
          command: 'read_meter',
          meterNumber,
          password,
          operatorCode,
          payload,
          topic: mqttTopic
        }
      });

      if (error) throw error;

      if (data?.success && data?.decoded?.data) {
        const meterData = data.decoded.data;
        setMeterReadResult(meterData);
        setMeterReadDialogOpen(true);
        
        // Also show balance in toast for quick view
        if (meterData.electricityBalance !== undefined) {
          toast({
            title: `✅ ${t('topup.meterReadSuccessful')}`,
            description: `${t('topup.currentBalance')}: ${meterData.electricityBalance.toFixed(2)} kWh`,
          });
        }
      } else {
        toast({
          variant: "destructive",
          title: `❌ ${t('topup.meterReadFailed')}`,
          description: data?.message || t('topup.meterReadFailedDesc'),
        });
      }
    } catch (error: any) {
      console.error('Meter read error:', error);
      toast({
        variant: "destructive",
        title: t('topup.meterReadFailed'),
        description: error?.message || t('topup.meterReadFailedDesc'),
      });
    } finally {
      setIsReadingMeter(false);
    }
  };

  const handleReload = async () => {
    const amount = getSelectedAmount();
    if (!amount || !user) {
      toast({
        variant: "destructive",
        title: t('topup.invalidAmount'),
        description: t('topup.invalidAmountDesc'),
      });
      return;
    }

    setIsReloading(true);
    try {
      // Generate unique recharge order number based on timestamp
      const generateRechargeOrderNumber = (): string => {
        const now = new Date();
        return now.getTime().toString().slice(-8); // Last 8 digits of timestamp
      };
      
      const controlCode = '14';
      const dataLength = '15';
      const dataIdentification = '04500105';
      const encodedDataId = encodeDlt645(dataIdentification);

      // Demo meter credentials
      const meterNumber = '002024023571';
      const password = '00000002';
      const operatorCode = '30303030';
      const pKey = 'TW0003'; // Demo meter pKey
      const encodedPassword = encodeDlt645(password);
      const encodedOperatorCode = encodeDlt645(operatorCode);

      const relayOperation = encodeDlt645('02'); // Force cut on — restore power on every reload (overdraft)
      
      // Generate unique recharge order number
      const rechargeId = encodeDlt645(generateRechargeOrderNumber());
      
      // Format recharge amount: kWh with 2 decimal places as cents
      // Example: 500 kWh -> 50000 cents -> "050000" -> prepend "00" (sign) -> "00050000"
      const kwhValue = getKwhFromAmount(amount);
      const kwhInCents = Math.floor(kwhValue * 100); // Convert to cents (500.00 -> 50000)
      const paddedKwh = kwhInCents.toString().padStart(6, '0'); // Pad to 6 digits
      const rechargeAmount = encodeDlt645(`00${paddedKwh}`); // 00 = positive charge

      const reverseMeterNum = reverseMeterNo(meterNumber);
      const bodyPayload = 
        START_OF_FRAME + 
        reverseMeterNum + 
        START_OF_FRAME + 
        controlCode + 
        dataLength + 
        encodedDataId + 
        encodedPassword + 
        encodedOperatorCode + 
        relayOperation + 
        rechargeId + 
        rechargeAmount;
      
      const checksum = calculateChecksum(bodyPayload);
      const payload = (PREAMBLE + bodyPayload + checksum + END_SYMBOL).toUpperCase();

      // Validate payload format
      const expectedPattern = 'FEFEFEFE 68 551900252001 68 14 15 38348337 35333333 6363636333636A4755 33333833 9A 16'.split(' ').join('');
      console.log('Recharge Payload:', payload);
      console.log('kWh to reload:', getKwhFromAmount(amount).toFixed(2));
      console.log(`Status recharge ${expectedPattern === payload ? '✅' : '❌'}`);
      
      // Construct full MQTT topic path
      const mqttTopic = `/device/electricmeter/${pKey}/${meterNumber}/data/down`;
      
      // Send command via edge function
      const { data, error } = await supabase.functions.invoke('mqtt-meter-control', {
        body: {
          meterId: 'demo-meter-001', // Demo meter ID
          command: 'recharge',
          meterNumber,
          password,
          operatorCode,
          payload,
          topic: mqttTopic
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        const decodedData = data.decoded?.data;
        const balanceKwh = decodedData?.electricityBalance ? 
          parseInt(decodeDlt645(decodedData.electricityBalance), 16) / 100 : null;
        const rechargedKwh = getKwhFromAmount(amount);
        
        toast({
          title: `✅ ${t('topup.rechargeSuccessful')}`,
          description: balanceKwh ?
            `${t('topup.rechargedKwh', { kwh: rechargedKwh.toFixed(2) })}\n${t('topup.newBalance', { kwh: balanceKwh.toFixed(2) })}` :
            t('topup.rechargeCommandSent', { kwh: rechargedKwh.toFixed(2) }),
        });
      } else {
        const decoded = data?.decoded;
        const errorDetails = decoded?.validations?.filter((v: any) => !v.valid).map((v: any) => v.field).join(', ');

        toast({
          variant: "destructive",
          title: `❌ ${t('topup.rechargeFailed')}`,
          description: errorDetails ?
            t('topup.rechargeFailedDetails', { details: errorDetails }) :
            (data?.message || t('topup.meterRejectedRecharge')),
        });
      }
    } catch (error: any) {
      console.error('Reload error:', error);
      toast({
        variant: "destructive",
        title: t('topup.reloadFailed'),
        description: error?.message || t('topup.reloadFailedDesc'),
      });
    } finally {
      setIsReloading(false);
    }
  };

  const handleQueryLiveBalance = async () => {
    if (!userMeterId || queryingLive) return;
    setQueryingLive(true);
    try {
      const result = await queryLiveMeterBalance(userMeterId);
      if (result.ok && result.balanceKwh != null) {
        setCurrentBalanceKwh(result.balanceKwh);
        setCurrentBalance(result.balanceKwh * PRICE_PER_KWH);
        toast({
          title: t("dashboard.liveBalanceOk"),
          description: t("dashboard.liveBalanceOkDesc", {
            kwh: result.balanceKwh.toFixed(2),
          }),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("dashboard.liveBalanceFailed"),
          description: result.message || t("dashboard.liveBalanceFailedDesc"),
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t("dashboard.liveBalanceFailed"),
        description: error?.message || t("dashboard.liveBalanceFailedDesc"),
      });
    } finally {
      setQueryingLive(false);
    }
  };

  return (
    <MainLayout title={t('topup.pageTitle')}>
      <div className="p-4 space-y-6">
        {/* Current Balance - Shows meter electricity balance */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white relative">
          {isRefreshing && (
            <div className="absolute top-2 right-2 flex items-center gap-1 text-xs bg-white/20 px-2 py-1 rounded-full">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span>{t('common.updating')}</span>
            </div>
          )}
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold mb-1">{t('dashboard.accountBalance')}</h2>
              <p className="text-3xl font-bold">{formatTSh(Number(currentBalance))}</p>
            </div>
            <div className="text-right">
              <p className="text-green-100 text-sm">{t('topup.electricity')}</p>
              <p className="text-2xl font-bold">{currentBalanceKwh.toFixed(2)} kWh</p>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-green-400/30 flex justify-between items-center text-sm gap-3">
            <span className="text-green-100">{t('topup.rate', { rate: formatTSh(PRICE_PER_KWH) })}</span>
            <div className="flex items-center gap-2">
              {userMeterId && (
                <button
                  type="button"
                  onClick={handleQueryLiveBalance}
                  disabled={queryingLive}
                  className="px-2.5 py-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-50 text-xs font-medium"
                >
                  {queryingLive ? t("dashboard.queryingBalance") : t("dashboard.queryLiveBalance")}
                </button>
              )}
              <span className="text-green-100 hidden sm:inline">
                {t('topup.lastTopUpShort', { date: lastTopupDate ? new Date(lastTopupDate).toLocaleDateString() : '—' })}
              </span>
              <button
                onClick={() => fetchData(true)}
                disabled={isRefreshing}
                className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-50"
                title={t('common.refreshData')}
              >
                <RefreshCw className={`h-4 w-4 text-white ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
{/* Check Balance Test Button - TEMPORARILY DISABLED
          <button
            onClick={handleZeroBalance}
            disabled={isReadingBalance}
            className="mt-3 flex items-center justify-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isReadingBalance ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span className="text-sm font-medium">Checking...</span>
              </>
            ) : (
              <>
                <Zap size={16} />
                <span className="text-sm font-medium">Check Balance (Test)</span>
              </>
            )}
          </button>
          */}
        </div>

        {/* Unit + meter being topped up (primary tenant and co-tenants) */}
        {topUpTarget && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-border/60">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              {t('topup.toppingUpFor')}
            </p>
            <div className="space-y-2.5">
              <div className="flex items-start gap-2.5">
                <Home className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">{t('topup.unitLabel')}</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-50 truncate">
                    {topUpTarget.unitLabel}
                    {topUpTarget.buildingName ? (
                      <span className="font-normal text-muted-foreground">
                        {" "}({topUpTarget.buildingName})
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Zap className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 space-y-1">
                  <p className="text-sm text-muted-foreground">{t('topup.meterLabel')}</p>
                  <p className="font-mono font-semibold text-gray-900 dark:text-gray-50">
                    {topUpTarget.meterNumber}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {topUpTarget.stsMeterNo && (
                      <Badge variant="outline" className="font-mono text-[10px] h-5">
                        STS {topUpTarget.stsMeterNo}
                      </Badge>
                    )}
                    {topUpTarget.isZhongyi && (
                      <Badge variant="secondary" className="text-[10px] h-5">ChirpStack</Badge>
                    )}
                    {topUpTarget.isSts && (
                      <Badge className="text-[10px] h-5 bg-amber-600 hover:bg-amber-600">STS</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Top-up Amounts */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">{t('topup.quickTopUp')}</h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {quickAmounts.map((amount) => (
              <button
                key={amount}
                onClick={() => {
                  setSelectedAmount(amount);
                  setCustomAmount("");
                }}
                className={`py-3 rounded-lg border-2 transition-colors
                  ${
                    selectedAmount === amount
                      ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-700 dark:text-white dark:border-blue-400"
                      : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  }`}
              >
                <p className="font-semibold">{formatTSh(amount)}</p>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">{t('topup.customAmount')}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-300">
                TZS
              </span>
              <input
                type="number"
                inputMode="numeric"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedAmount(null);
                }}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                  amountOutOfRange && !selectedAmount
                    ? "border-red-500 dark:border-red-500"
                    : "border-gray-300 dark:border-gray-700"
                }`}
                placeholder={t('topup.enterAmount')}
                min={MIN_TOPUP_TZS}
                max={MAX_TOPUP_TZS}
              />
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t('topup.amountRangeHint', {
                min: formatTSh(MIN_TOPUP_TZS),
                max: formatTSh(MAX_TOPUP_TZS),
              })}
            </p>
            {amountOutOfRange && (
              <p className="mt-1.5 text-sm text-red-600 dark:text-red-400" role="alert">
                {t('topup.amountOutOfRange', {
                  min: formatTSh(MIN_TOPUP_TZS),
                  max: formatTSh(MAX_TOPUP_TZS),
                })}
              </p>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        {selectedAmt > 0 && amountOutOfRange && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-800 dark:text-red-300">
            {t('topup.amountOutOfRange', {
              min: formatTSh(MIN_TOPUP_TZS),
              max: formatTSh(MAX_TOPUP_TZS),
            })}
          </div>
        )}
        {amountValid && !userMeterId && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 text-sm text-yellow-800 dark:text-yellow-300">
            {t('topup.noMeterLinked')}
          </div>
        )}
        {amountValid && userMeterId && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">{t('topup.choosePaymentMethod')}</h3>
            <PaymentMethodSelector
              amount={selectedAmt}
              userId={user?.id}
              meterId={userMeterId || undefined}
              source="tenant"
              onSuccess={(data) => {
                toast({
                  title: t('topup.paymentInitiated'),
                  description: t('topup.paymentInitiatedDesc'),
                });
              }}
              onPaymentConfirmed={() => {
                refreshBalanceAfterPayment();
              }}
            />
          </div>
        )}

        {/* kWh Info & Test Reload Button - TEMPORARILY DISABLED
        {getSelectedAmount() > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800 space-y-3">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Energy Details</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Price: <span className="font-semibold text-gray-900 dark:text-gray-100">{formatTSh(PRICE_PER_KWH)} per kWh</span>
                </p>
                <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                  You'll get: {getKwhFromAmount(getSelectedAmount()).toFixed(2)} kWh
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleReload}
                disabled={isReloading}
                className="flex items-center justify-center space-x-2 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isReloading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span className="font-semibold">Loading...</span>
                  </>
                ) : (
                  <>
                    <Zap size={20} />
                    <span className="font-semibold">Test Reload</span>
                  </>
                )}
              </button>
              <button
                onClick={handleSinglePhaseRead}
                disabled={isReadingMeter}
                className="flex items-center justify-center space-x-2 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isReadingMeter ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span className="font-semibold">Reading...</span>
                  </>
                ) : (
                  <>
                    <Zap size={20} />
                    <span className="font-semibold">Read Meter</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        */}

        {/* Meter Read Result Dialog */}
        <Dialog open={meterReadDialogOpen} onOpenChange={setMeterReadDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('topup.meterDataReading')}</DialogTitle>
            </DialogHeader>
            {meterReadResult && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('topup.meterNumber')}</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{meterReadResult.meterNumber}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('topup.currentBalance')}</p>
                    <p className="font-semibold text-green-700 dark:text-green-300">{meterReadResult.electricityBalance?.toFixed(2)} kWh</p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.combinedActiveTotal')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.combinedActiveTotal?.toFixed(2)} kWh</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.overdrawnBalance')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.overdrawnBalance?.toFixed(2)} kWh</span>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('topup.electricalParameters')}</h4>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.voltageA')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.voltageA?.toFixed(1)} V</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.currentA')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.currentA?.toFixed(3)} A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.activePower')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.activePower?.toFixed(4)} kW</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.gridFrequency')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.gridFrequency?.toFixed(2)} Hz</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.powerFactor')}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{meterReadResult.powerFactor?.toFixed(3)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('topup.statusBits')}</span>
                    <span className="font-mono text-xs text-gray-900 dark:text-gray-100">{meterReadResult.statusBits}</span>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Recharge Link Section - Share link for others to top up */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
          <div className="text-center space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('topup.needHelpTopping')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('topup.shareLinkDesc')}
            </p>
            <RechargeLinkDialog />
          </div>
        </div>

        {/* Recent Top-ups */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('topup.recentTopUps')}</h3>

          <div className="space-y-3">
            {loadingTransactions ? (
              <div className="text-center p-4 text-gray-500">{t('common.loading')}</div>
            ) : recentTransactions.length > 0 ? (
              recentTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Plus size={20} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">TZS {Number(transaction.amount).toLocaleString()}</p>
                      <p className="text-sm text-gray-500">{t('topup.mobileMoney')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={transaction.status === "completed" ? "default" : "secondary"}
                      className="capitalize text-xs"
                    >
                      {t('common.completed')}
                    </Badge>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(transaction.effective_at || transaction.completed_at || transaction.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center p-4 text-gray-500">{t('topup.noRecentTransactions')}</div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TopUp;
